import { useMemo } from 'react';
import { getLocale } from 'umi';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';

export interface ITitleDesConfig {
  title?: string;
  titleCn?: string;
  contentEn?: string;
  contentCn?: string;
  explainEn?: string;
  explainCn?: string;
  // 七彩石 key 的值
  rainbowKey?: string;
  // 是否使用七彩石内容
  isRainbow?: boolean;
}

export interface ITitleDesResult {
  title: string;
  content: string;
}

export interface IUseTitleDesParams {
  titleDesConfig?: ITitleDesConfig[];
  explainInfo?: any;
  isEN?: boolean;
}

// 该组件用来处理卡片旁边 information icon 的气泡提示的相关逻辑，配合 components/TitleDes/index.tsx 使用
export const useTitleDes = (params: IUseTitleDesParams): ITitleDesResult[] => {
  const { titleDesConfig, explainInfo, isEN = false } = params;

  const titleDes = useMemo(() => {
    if (!titleDesConfig || !Array.isArray(titleDesConfig)) {
      return [];
    }

    const processedTitleDes = titleDesConfig.map((val: ITitleDesConfig) => {
      if (!val) return null;

      const {
        title,
        titleCn,
        contentEn,
        contentCn,
        explainEn,
        explainCn,
        isRainbow,
        rainbowKey,
      } = val;

      // 从配置中获取文本
      const configContent = isEN ? contentEn : contentCn;

      // 从七彩石获取文本
      const rainbowContent = getExplainVal(
        explainInfo,
        rainbowKey || '',
        getLocale(),
        explainEn,
        explainCn,
      );

      // 如果没有任何内容，返回null
      if (!rainbowContent && !configContent) {
        return null;
      }

      let content: string;

      if (isRainbow) {
        // 优先使用七彩石内容，fallback到配置内容
        content = rainbowContent || configContent || '';
      } else {
        // 直接使用配置内容
        content = configContent || '';
      }

      return {
        title: (isEN ? title || titleCn : titleCn || title) || '',
        content,
      };
    });

    // 过滤掉null值
    return processedTitleDes.filter(
      (val): val is ITitleDesResult => val !== null,
    );
  }, [titleDesConfig, explainInfo, isEN]);

  return titleDes;
};

export default useTitleDes;
